import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LEGAL_CONFIG from "../config/legalConfig";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";

function buildText(doc) {
  return doc.sections.map((s) => s.heading + "\n" + s.body).join("\n\n");
}

function nowThaiTime() {
  const now = new Date();
  const thai = new Date(now.getTime() + 7 * 60 * 60 * 1000);
  return thai.toISOString().replace("Z", "+07:00");
}

function TermsModal({
  title,
  content,
  hasScrolledToBottom,
  setHasScrolledToBottom,
  setAgreed,
  setModal,
}) {
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 700,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
            style={{
                padding: "18px 22px",
                borderBottom: "1px solid #e5e7eb",
                textAlign: "center",
            }}
            >
            <h5
                style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#111",
                }}
            >
                {title}
            </h5>
            </div>

        <div
          onScroll={handleScroll}
          style={{
            padding: "20px",
            overflowY: "auto",
            flex: 1,
            whiteSpace: "pre-wrap",
            lineHeight: 1.8,
            fontSize: 14,
            color: "#444",
          }}
        >
          {content}
        </div>

        <div
            style={{
                padding: "16px 20px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
            }}
            >
            <span
                style={{
                fontSize: 12,
                color: hasScrolledToBottom
                    ? "#16a34a"
                    : "#dc2626",
                }}
            >
                {hasScrolledToBottom
                ? "✓ อ่านครบแล้ว"
                : "กรุณาเลื่อนลงจนสุด"}
            </span>

            <button
                disabled={!hasScrolledToBottom}
                onClick={() => {
                setAgreed(true);
                setModal(false);
                }}
                style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: 8,
                background: hasScrolledToBottom
                    ? "#111827"
                    : "#d1d5db",
                color: "#fff",
                cursor: hasScrolledToBottom
                    ? "pointer"
                    : "not-allowed",
                }}
            >
                ยอมรับ
            </button>
            </div>
      </div>
    </div>
  );
}

export default function OAuthConsent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const token = searchParams.get("token");
  const role = searchParams.get("role");
  const provider = searchParams.get("provider") || "google";

  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(true);
  const [hasScrolledToBottom, setHasScrolledToBottom] =
    useState(false);

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  const allLegalContent =
    buildText(LEGAL_CONFIG.terms) +
    "\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n" +
    buildText(LEGAL_CONFIG.privacy);

  const handleAccept = async () => {
    if (!agreed) return;

    setLoading(true);

    try {
      await fetch(
        "/api/profile/consent",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            termsAcceptedAt: nowThaiTime(),
            privacyAcceptedAt: nowThaiTime(),
          }),
        }
      );

      localStorage.setItem("token", token);

      if (role) {
        localStorage.setItem("role", role);
      }

      navigate("/rooms");
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleDecline = () => {
    navigate("/login");
  };

  const providerLabel = "Google";
  const providerIcon = "🌐";

  return (
    <>
      {modal && (
        <TermsModal
            title={t(lang, "oauthModalTitle")}
            content={allLegalContent}
            hasScrolledToBottom={hasScrolledToBottom}
            setHasScrolledToBottom={setHasScrolledToBottom}
            setAgreed={setAgreed}
            setModal={setModal}
            />
      )}

      <div
        style={{
          minHeight: "100vh",
          background: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          fontFamily:
            "'Noto Sans Thai', sans-serif",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            padding: "36px 32px",
            maxWidth: 440,
            width: "100%",
            boxShadow:
              "0 4px 24px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              textAlign: "center",
              marginBottom: 24,
            }}
          >
            <img
              src="/logo.png"
              width={72}
              alt="logo"
              style={{ marginBottom: 10 }}
            />

            <div
              style={{
                fontSize: 13,
                color: "#6b7280",
              }}
            >
              {providerIcon}
              {" "}
              {t(lang, "oauthLoginWith")} {providerLabel}
            </div>
          </div>

          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#111",
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {t(lang, "oauthTitle")}
          </h2>

          <p
            style={{
              fontSize: 13,
              color: "#6b7280",
              textAlign: "center",
              marginBottom: 24,
              lineHeight: 1.6,
            }}
          >
            {t(lang, "oauthSubtitle")}
            {" "}
            {LEGAL_CONFIG.companyName}
            <br />
            {t(lang, "oauthSubtitle2")}
          </p>

          <button
            onClick={() => setModal(true)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: 10,
              border: "1px solid #d1d5db",
              background: "#f9fafb",
              cursor: "pointer",
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            {t(lang, "oauthReadBtn")}
          </button>

          {!hasScrolledToBottom && (
            <div
              style={{
                background: "#FEF3C7",
                color: "#92400E",
                padding: "10px 12px",
                borderRadius: 8,
                fontSize: 12,
                marginBottom: 15,
              }}
            >
              {t(lang, "oauthScrollHint")}
            </div>
          )}

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 24,
              cursor: hasScrolledToBottom
                ? "pointer"
                : "not-allowed",
            }}
          >
            <input
              type="checkbox"
              checked={agreed}
              disabled={!hasScrolledToBottom}
              onChange={(e) =>
                setAgreed(e.target.checked)
              }
              style={{
                width: 17,
                height: 17,
                marginTop: 2,
                accentColor: "#111",
              }}
            />

            <span
              style={{
                fontSize: 13,
                color: "#374151",
                lineHeight: 1.6,
              }}
            >
              {t(lang, "oauthAgreeText")}
            </span>
          </label>

          <button
            onClick={handleAccept}
            disabled={!agreed || loading}
            style={{
              width: "100%",
              height: 44,
              borderRadius: 10,
              border: "none",
              background: agreed
                ? "#111827"
                : "#e5e7eb",
              color: agreed
                ? "#fff"
                : "#9ca3af",
              fontSize: 14,
              fontWeight: 700,
              cursor: agreed
                ? "pointer"
                : "not-allowed",
              marginBottom: 10,
            }}
          >
            {loading ? t(lang, "processing") : t(lang, "acceptAndStart")}
          </button>

          <button
            onClick={handleDecline}
            style={{
              width: "100%",
              height: 38,
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: "none",
              fontSize: 13,
              color: "#6b7280",
              cursor: "pointer",
            }}
          >
            {t(lang, "cancelAndLeave")}
          </button>
        </div>
      </div>
    </>
  );
}
